import { useState, useEffect } from 'react';
import {
  fetchListings,
  approveListing,
  rejectListing,
  deleteListing,
  getListingStats,
  type ListingFilters,
} from '@/api/marketplace-listings.api';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  CheckCircle,
  XCircle,
  Trash2,
  ShoppingBag,
  TrendingUp,
  AlertCircle,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export default function MarketplaceListings() {
  const [listings, setListings] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<ListingFilters>({
    status: null,
    limit: 20,
  });
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [selectedListing, setSelectedListing] = useState<any>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'delete'>('approve');
  const [actionReason, setActionReason] = useState('');

  const loadListings = async (loadMore = false) => {
    try {
      setLoading(true);
      const currentFilters = loadMore ? { ...filters, cursor: nextCursor } : filters;
      const data = await fetchListings(currentFilters);
      if (loadMore) {
        setListings((prev) => [...prev, ...(data?.data || [])]);
      } else {
        setListings(data?.data || []);
      }
      setNextCursor(data?.nextCursor || null);
      setHasMore(data?.hasMore || false);
    } catch (error) {
      console.error('Failed to load listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await getListingStats();
      console.log(data, 'listing status')
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  useEffect(() => {
    loadListings();
    loadStats();
  }, [filters]);

  const openActionDialog = (
    listing: any,
    action: 'approve' | 'reject' | 'delete'
  ) => {
    setSelectedListing(listing);
    setActionType(action);
    setActionReason('');
    setActionDialogOpen(true);
  };

  const handleAction = async () => {
    if (!selectedListing) return;

    try {
      if (actionType === 'approve') {
        await approveListing(selectedListing._id);
      } else if (actionType === 'reject') {
        await rejectListing(selectedListing._id, actionReason);
      } else if (actionType === 'delete') {
        await deleteListing(selectedListing._id, actionReason);
      }
      setActionDialogOpen(false);
      setSelectedListing(null);
      setActionReason('');
      loadListings();
      loadStats();
    } catch (error) {
      console.error('Failed to perform action:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: any = {
      pending: { variant: 'secondary', icon: AlertCircle, color: 'text-yellow-600' },
      active: { variant: 'default', icon: CheckCircle, color: 'text-green-600' },
      rejected: { variant: 'destructive', icon: XCircle, color: 'text-red-600' },
      sold: { variant: 'outline', icon: ShoppingBag, color: 'text-blue-600' },
      exchanged: { variant: 'outline', icon: ShoppingBag, color: 'text-purple-600' },
      expired: { variant: 'outline', icon: AlertCircle, color: 'text-gray-600' },
      removed: { variant: 'destructive', icon: Trash2, color: 'text-red-500' },
      draft: { variant: 'secondary', icon: AlertCircle, color: 'text-gray-400' },
      inactive: { variant: 'outline', icon: XCircle, color: 'text-gray-500' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className={`w-3 h-3 ${config.color}`} />
        {status}
      </Badge>
    );
  };

  return (
    <div className="container mx-auto p-6 space-y-6 h-full overflow-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Marketplace Listings</h1>
          <p className="text-muted-foreground">
            Review and moderate marketplace listings
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.pending || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.active || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Sold</CardTitle>
              <ShoppingBag className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.sold || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total || 0}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Filter listings by status and type</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-4">
          <Select
            value={filters.status || 'all'}
            onValueChange={(value) =>
              setFilters({ ...filters, status: value === 'all' ? undefined : value as any })
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="sold">Sold</SelectItem>
              <SelectItem value="exchanged">Exchanged</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="removed">Removed</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.listingType || 'all'}
            onValueChange={(value) =>
              setFilters({ ...filters, listingType: value === 'all' ? undefined : value as any })
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="marketplace">Marketplace</SelectItem>
              <SelectItem value="exchange">Exchange</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Listings Table */}
      <Card>
        <CardHeader>
          <CardTitle>Listings</CardTitle>
          <CardDescription>
            {listings.length} listing(s) found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No listings found
            </div>
          ) : (
            <div className="overflow-auto max-h-[600px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Image</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Seller</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listings.map((listing) => (
                  <TableRow key={listing._id}>
                    <TableCell>
                      <img
                        src={listing.media?.[0]?.url}
                        alt={listing.title}
                        className="w-16 h-16 object-cover rounded"
                      />
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <div className="font-medium line-clamp-2">{listing.title}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <img
                          src={listing.seller?.profile || '/avatar.png'}
                          alt={listing.seller?.username}
                          className="w-6 h-6 rounded-full"
                        />
                        {listing.seller?.username}
                      </div>
                    </TableCell>
                    <TableCell>
                      {listing.price ? (
                        <span className="font-semibold">${listing.price}</span>
                      ) : (
                        <span className="text-purple-600">Exchange</span>
                      )}
                    </TableCell>
                    <TableCell className="capitalize">{listing.listingType}</TableCell>
                    <TableCell>{listing.category?.name || '-'}</TableCell>
                    <TableCell>{getStatusBadge(listing.status)}</TableCell>
                    <TableCell>
                      {new Date(listing.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {listing.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => openActionDialog(listing, 'approve')}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => openActionDialog(listing, 'reject')}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openActionDialog(listing, 'delete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
          {hasMore && !loading && (
            <div className="flex justify-center pt-4">
              <Button variant="outline" onClick={() => loadListings(true)}>
                Load More
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent className="sm:max-w-[525px]">
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' && 'Approve Listing'}
              {actionType === 'reject' && 'Reject Listing'}
              {actionType === 'delete' && 'Delete Listing'}
            </DialogTitle>
            <DialogDescription>
              {actionType === 'approve' && 'Make this listing visible in the marketplace'}
              {actionType === 'reject' && 'Reject this listing with a reason'}
              {actionType === 'delete' && 'Permanently delete this listing'}
            </DialogDescription>
          </DialogHeader>

          {selectedListing && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-muted-foreground">Listing</Label>
                <div className="font-medium">{selectedListing.title}</div>
              </div>

              {(actionType === 'reject' || actionType === 'delete') && (
                <div className="space-y-2">
                  <Label htmlFor="actionReason">Reason *</Label>
                  <Textarea
                    id="actionReason"
                    placeholder="Explain why this action is being taken..."
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    rows={3}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAction}
              disabled={(actionType === 'reject' || actionType === 'delete') && !actionReason.trim()}
              variant={actionType === 'approve' ? 'default' : 'destructive'}
            >
              {actionType === 'approve' && 'Approve'}
              {actionType === 'reject' && 'Reject'}
              {actionType === 'delete' && 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
